import { useEffect, useRef, useState } from 'react';

// Curated palette of visually distinct fleet colours. Ordered so that the
// first six match the historical FLEET_COLORS seed (callers cycle through this
// list when auto-assigning a colour to a new fleet); the extra six widen the
// distinct set before colours start repeating. All chosen for good contrast on
// a white background and easy telling-apart at a glance on the water.
export const FLEET_COLORS = [
  '#0066cc', // blue
  '#cc0000', // red
  '#009933', // green
  '#ff9900', // orange
  '#6633cc', // purple
  '#cc6699', // pink
  '#00a3a3', // teal
  '#e6b800', // gold
  '#cc00cc', // magenta
  '#5cb800', // lime
  '#8b5a2b', // brown
  '#1a1a2e', // ink
];

export function normalizeHex(value) {
  return (value || '').trim().toLowerCase();
}

/**
 * Colour field with a palette/swatch mode. A compact trigger shows the current
 * colour; clicking it opens a popover with one-click preset swatches plus a
 * native colour input for a custom choice. Keeps the field small enough to live
 * in a table cell while making it fast to land on a clean, distinct colour.
 */
export default function ColorPicker({ value, onChange, colors = FLEET_COLORS, label = 'colour' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = normalizeHex(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (color) => {
    onChange(color);
    setOpen(false);
  };

  return (
    <div className="color-picker" ref={ref}>
      <button
        type="button"
        className="color-picker-trigger"
        style={{ backgroundColor: value || '#0066cc' }}
        aria-label={`Choose ${label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={value || 'Choose colour'}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div className="color-picker-popover" role="dialog" aria-label={`Choose ${label}`}>
          <div className="color-swatches">
            {colors.map((color) => {
              const selected = normalizeHex(color) === current;
              return (
                <button
                  key={color}
                  type="button"
                  className={`color-swatch${selected ? ' color-swatch--selected' : ''}`}
                  style={{ backgroundColor: color }}
                  aria-label={color}
                  aria-pressed={selected}
                  title={color}
                  onClick={() => pick(color)}
                />
              );
            })}
          </div>
          <label className="color-picker-custom">
            <span className="color-picker-custom-label">Custom</span>
            <input
              type="color"
              value={value || '#0066cc'}
              onChange={(e) => onChange(e.target.value)}
              className="color-input"
              aria-label="Custom colour"
            />
          </label>
        </div>
      )}
    </div>
  );
}
